import React, { useState } from "react";
import {
  FaInfoCircle,
  FaRedo,
  FaCheck,
  FaPencilAlt,
  FaEnvelope,
} from "react-icons/fa";

const SubjectLines = ({ campaign, onChange, onEmailSettingChange }) => {
  const [activeTab, setActiveTab] = useState("generated");
  const [editingIndex, setEditingIndex] = useState(null);
  const [customSubject, setCustomSubject] = useState("");

  // 5-touch campaign sequence with predefined subject lines
  const sequenceSubjects = [
    {
      day: 0,
      name: "Spark",
      subject: "Cut Landfill Fees 30%? Quick intro from ReWorld",
      description: "Introduce and highlight one key result",
    },
    {
      day: 3,
      name: "Value Add",
      subject: "60-sec video: How [peer] hit zero-waste targets",
      description: "Share mini case study and video link",
    },
    {
      day: 6,
      name: "Pain/Insight",
      subject: "Hidden cost on your waste invoice?",
      description: "Highlight hidden costs, ask engaging question",
    },
    {
      day: 10,
      name: "Social Proof",
      subject: "[Peer] saved $X in hauling—see the numbers?",
      description: "Share quantified peer outcome, offer calendar link",
    },
    {
      day: 14,
      name: "Break-up",
      subject: "Should I close the file?",
      description: "Friendly final outreach with easy out",
    },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onChange({ target: { name, value } });
  };

  const handleCheckboxChange = (setting) => {
    onEmailSettingChange(setting, !campaign.emailSettings[setting]);
  };

  const startEditing = (index, initialValue) => {
    setEditingIndex(index);
    setCustomSubject(initialValue);
  };

  const saveEdit = (index) => {
    // In a real implementation, this would update the specific subject line
    // For this demo, we'll just update the main subject line
    onChange({
      target: {
        name: "subjectLine",
        value: customSubject,
      },
    });
    setEditingIndex(null);
  };

  const regenerateSubject = (index) => {
    // In a real implementation, this would call an AI service to regenerate
    // a specific subject line. For now, we'll just show an alert.
    alert(`Regenerating subject line for ${sequenceSubjects[index].name}`);
  };

  return (
    <div>
      <h2 className="text-xl font-semibold mb-6">Subject lines</h2>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex">
          <button
            onClick={() => setActiveTab("generated")}
            className={`py-2 px-4 font-medium ${
              activeTab === "generated"
                ? "border-b-2 border-purple-600 text-purple-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Generated
          </button>
          <button
            onClick={() => setActiveTab("custom")}
            className={`py-2 px-4 font-medium ${
              activeTab === "custom"
                ? "border-b-2 border-purple-600 text-purple-600"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Custom
          </button>
        </div>
      </div>

      {activeTab === "generated" ? (
        <div>
          {/* Email Customization Options */}
          <div className="space-y-4 mb-6">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="includeEmojis"
                checked={campaign.emailSettings.includeEmojis}
                onChange={() => handleCheckboxChange("includeEmojis")}
                className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <label
                htmlFor="includeEmojis"
                className="ml-2 text-sm text-gray-700"
              >
                Include emojis
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="includeLeadName"
                checked={campaign.emailSettings.includeLeadName}
                onChange={() => handleCheckboxChange("includeLeadName")}
                className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <label
                htmlFor="includeLeadName"
                className="ml-2 text-sm text-gray-700"
              >
                Include lead's name
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="includeCompanyName"
                checked={campaign.emailSettings.includeCompanyName}
                onChange={() => handleCheckboxChange("includeCompanyName")}
                className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <label
                htmlFor="includeCompanyName"
                className="ml-2 text-sm text-gray-700"
              >
                Include lead's company name
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="useAllLowercase"
                checked={campaign.emailSettings.useAllLowercase}
                onChange={() => handleCheckboxChange("useAllLowercase")}
                className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <label
                htmlFor="useAllLowercase"
                className="ml-2 text-sm text-gray-700"
              >
                Use all lowercase letters
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="usePlainLanguage"
                checked={campaign.emailSettings.usePlainLanguage}
                onChange={() => handleCheckboxChange("usePlainLanguage")}
                className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <label
                htmlFor="usePlainLanguage"
                className="ml-2 text-sm text-gray-700"
              >
                Use plain language
              </label>
            </div>
          </div>

          {/* Subject Lines for each touchpoint */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center">
              <FaEnvelope className="mr-2 text-purple-600" />
              5-Touch Campaign Subject Lines
            </h3>

            <div className="space-y-4">
              {sequenceSubjects.map((touchpoint, index) => (
                <div key={index} className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b flex justify-between items-center">
                    <div>
                      <span className="font-medium text-gray-800">
                        Day {touchpoint.day}: {touchpoint.name}
                      </span>
                      <span className="ml-2 text-xs text-gray-500">
                        {touchpoint.description}
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => regenerateSubject(index)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Regenerate subject"
                      >
                        <FaRedo size={14} />
                      </button>
                      <button
                        onClick={() => startEditing(index, touchpoint.subject)}
                        className="text-gray-600 hover:text-gray-800"
                        title="Edit subject line"
                      >
                        <FaPencilAlt size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="p-4">
                    {editingIndex === index ? (
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={customSubject}
                          onChange={(e) => setCustomSubject(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                        />
                        <button
                          onClick={() => saveEdit(index)}
                          className="bg-green-500 text-white p-2 rounded-md hover:bg-green-600"
                        >
                          <FaCheck size={14} />
                        </button>
                      </div>
                    ) : (
                      <p className="text-gray-800">{touchpoint.subject}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Regenerate Button */}
          <button className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors mb-6">
            Regenerate All Subject Lines
          </button>
        </div>
      ) : (
        <div>
          {/* Custom Subject Lines */}
          <div className="space-y-4 mb-6">
            {sequenceSubjects.map((touchpoint, index) => (
              <div key={index} className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Day {touchpoint.day}: {touchpoint.name}
                </label>
                <input
                  type="text"
                  defaultValue={touchpoint.subject}
                  placeholder={`Enter subject line for ${touchpoint.name}`}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {touchpoint.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Open Rate Tracking */}
      <div className="border-t border-gray-200 pt-6 mt-6">
        <div className="flex items-center mb-2">
          <input
            type="checkbox"
            id="trackOpenRate"
            checked={campaign.emailSettings.trackOpenRate}
            onChange={() => handleCheckboxChange("trackOpenRate")}
            className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
          />
          <label
            htmlFor="trackOpenRate"
            className="ml-2 font-medium text-gray-700"
          >
            Enable open rate tracking
          </label>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 flex items-start">
          <FaInfoCircle className="text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
          <p className="text-sm text-blue-700">
            Tracking open rate may impact deliverability by causing emails to be
            sent to spam
          </p>
        </div>
      </div>

      {/* Preview Email Button */}
      <div className="mt-6">
        <button className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors">
          Preview Email Sequence
        </button>
      </div>
    </div>
  );
};

export default SubjectLines;
